import FixedPlugin from "components/fixedPlugin/FixedPlugin";
import React from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import routes from "routes";
import { getToken } from "utils/auth";
import { getSettings } from "api/settings";
import bm2payLogo from "../../assets/img/bm2-white.svg";
import lightningChecksLogo from "../../assets/img/mainlogo.png";
import paxumLogo from "../../assets/img/paxum.png";

const hostname = window.location.hostname;

const logos: any = {
  "gateway-dev.lightningchecks.com": lightningChecksLogo,
  "gateway.lightningchecks.com": lightningChecksLogo,
  "testing.lightningchecks.com": lightningChecksLogo,
  "gateway-dev.bm2pay.com": bm2payLogo,
  "gateway.bm2pay.com": bm2payLogo,
  "gateway-stagging.bm2pay.com": bm2payLogo,
  "pay.paxum.com": paxumLogo,
};

const accentColors: any = {
  "gateway-dev.lightningchecks.com": { from: "#1e3a5f", to: "#2563eb" },
  "gateway.lightningchecks.com":     { from: "#1e3a5f", to: "#2563eb" },
  "testing.lightningchecks.com":     { from: "#1e3a5f", to: "#2563eb" },
  "gateway-dev.bm2pay.com":          { from: "#0f2744", to: "#1d4ed8" },
  "gateway.bm2pay.com":              { from: "#0f2744", to: "#1d4ed8" },
  "gateway-stagging.bm2pay.com":     { from: "#0f2744", to: "#1d4ed8" },
  "pay.paxum.com":                   { from: "#1a2f4a", to: "#0ea5e9" },
};

const defaultAccent = { from: "#0f2744", to: "#1d4ed8" };

export default function Auth() {
  const [loginIcon, setLoginIcon] = React.useState<string | null>(logos[hostname] || bm2payLogo);
  const [accent, setAccent] = React.useState(accentColors[hostname] || defaultAccent);
  const navigate = useNavigate();

  React.useEffect(() => {
    const token = getToken();
    if (token) navigate("/admin/dashboard");
  }, [navigate]);

  React.useEffect(() => {
    getSettings(false)
      .then((data) => {
        if (data.login_icon) setLoginIcon(data.login_icon);
        if (data.gradient_from_color && data.gradient_to_color) {
          setAccent({ from: data.gradient_from_color, to: data.gradient_to_color });
        }
      })
      .catch(() => {});
  }, []);

  const getRoutes = (routes: RoutesType[]): any =>
    routes.map((prop, key) =>
      prop.layout === "/auth" ? (
        <Route path={`/${prop.path}`} element={prop.component} key={key} />
      ) : null
    );

  document.documentElement.dir = "ltr";

  const panelGradient = `linear-gradient(160deg, ${accent.from} 0%, ${accent.to} 100%)`;

  return (
    <div className="auth-root">
      {/* <FixedPlugin /> */}

      {/* ── Left: form area ── */}
      <div className="auth-form-side">
        

        <div className="auth-form-center">
          <Routes>
            {getRoutes(routes)}
            <Route path="/" element={<Navigate to="/auth/sign-in" replace />} />
          </Routes>
        </div>

      </div>

      {/* ── Right: branded panel ── */}
      <div className="auth-brand-side" style={{ background: panelGradient }}>
        {/* Geometric pattern overlay */}
        <svg className="auth-geo-pattern" viewBox="0 0 600 700" fill="none" preserveAspectRatio="xMidYMid slice">
          <circle cx="500" cy="100" r="220" stroke="rgba(255,255,255,0.06)" strokeWidth="1"/>
          <circle cx="500" cy="100" r="160" stroke="rgba(255,255,255,0.06)" strokeWidth="1"/>
          <circle cx="500" cy="100" r="100" stroke="rgba(255,255,255,0.06)" strokeWidth="1"/>
          <circle cx="100" cy="600" r="180" stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
          <circle cx="100" cy="600" r="120" stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
          <line x1="0" y1="0" x2="600" y2="700" stroke="rgba(255,255,255,0.03)" strokeWidth="1"/>
          <line x1="600" y1="0" x2="0" y2="700" stroke="rgba(255,255,255,0.03)" strokeWidth="1"/>
          <rect x="60" y="200" width="120" height="80" rx="12" stroke="rgba(255,255,255,0.08)" strokeWidth="1" fill="rgba(255,255,255,0.04)"/>
          <rect x="380" y="400" width="140" height="90" rx="12" stroke="rgba(255,255,255,0.08)" strokeWidth="1" fill="rgba(255,255,255,0.04)"/>
          <circle cx="300" cy="350" r="8" fill="rgba(255,255,255,0.12)"/>
          <circle cx="200" cy="500" r="5" fill="rgba(255,255,255,0.1)"/>
          <circle cx="420" cy="180" r="6" fill="rgba(255,255,255,0.1)"/>
        </svg>

        {/* Floating payment card illustration */}
        <div className="auth-card-illustration">
          <div className="auth-pay-card auth-pay-card--back" />
          <div className="auth-pay-card auth-pay-card--front">
            <div className="auth-pay-card-top">
              <div className="auth-pay-chip">
                <div className="auth-pay-chip-line" />
                <div className="auth-pay-chip-line" />
                <div className="auth-pay-chip-line" />
              </div>
              <svg width="32" height="22" viewBox="0 0 32 22" fill="none" style={{opacity:0.9}}>
                <circle cx="12" cy="11" r="11" fill="rgba(255,255,255,0.4)"/>
                <circle cx="20" cy="11" r="11" fill="rgba(255,255,255,0.25)"/>
              </svg>
            </div>
            <div className="auth-pay-number">•••• •••• •••• 4291</div>
            <div className="auth-pay-card-bottom">
              <div>
                <div className="auth-pay-micro">CARD HOLDER</div>
                <div className="auth-pay-name">John Merchant</div>
              </div>
              <div style={{textAlign:"right"}}>
                <div className="auth-pay-micro">EXPIRES</div>
                <div className="auth-pay-name">09 / 28</div>
              </div>
            </div>
          </div>

          {/* Transaction pill */}
          <div className="auth-txn-pill auth-txn-pill--1">
            <div className="auth-txn-icon auth-txn-icon--green">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
            <div>
              <div className="auth-txn-label">Payment received</div>
              <div className="auth-txn-amount">+$4,250.00</div>
            </div>
          </div>

          <div className="auth-txn-pill auth-txn-pill--2">
            <div className="auth-txn-icon auth-txn-icon--blue">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
            </div>
            <div>
              <div className="auth-txn-label">Settlement</div>
              <div className="auth-txn-amount">$12,800.00</div>
            </div>
          </div>
        </div>

        {/* Bottom brand block */}
        <div className="auth-brand-bottom">
          {loginIcon && (
            <img src={loginIcon} alt="Logo" className="auth-brand-logo" />
          )}
          <p className="auth-brand-tagline">
            Enterprise-grade payment<br />infrastructure you can trust.
          </p>
          <div className="auth-trust-row">
            {[
              { icon: "🔒", label: "PCI DSS Level 1" },
              { icon: "🛡️", label: "256-bit SSL" },
              { icon: "⚡", label: "99.99% Uptime" },
            ].map((t) => (
              <div key={t.label} className="auth-trust-badge">
                <span style={{fontSize:"14px"}}>{t.icon}</span>
                <span>{t.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=Fraunces:ital,opsz,wght@0,9..144,300;1,9..144,300&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .auth-root {
          font-family: 'Plus Jakarta Sans', sans-serif;
          display: flex;
          min-height: 100vh;
          background: #f5f6fa;
        }

        /* ══ FORM SIDE ══ */
        .auth-form-side {
          position: relative;
          flex: 0 0 52%;
          display: flex;
          flex-direction: column;
          background: #ffffff;
          overflow-y: auto;
        }

        .auth-mobile-bar {
          display: none;
          align-items: center;
          justify-content: center;
          padding: 20px 24px;
          min-height: 80px;
        }
        .auth-mobile-logo { height: 32px; width: auto; object-fit: contain; }

        .auth-desktop-topbar {
          padding: 28px 48px 0;
          flex-shrink: 0;
        }
        .auth-desktop-brand {
          display: flex; align-items: center; gap: 8px;
        }
        .auth-brand-dot {
          width: 10px; height: 10px; border-radius: 50%;
        }
        .auth-brand-label {
          font-size: 13px; font-weight: 600;
          color: #374151; letter-spacing: 0.2px;
        }

        .auth-form-center {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 40px 48px;
        }

        .auth-form-footer {
          padding: 20px 48px 28px;
          display: flex; align-items: center; gap: 10px;
          font-size: 12px; color: #9ca3af;
          flex-shrink: 0;
        }
        .auth-footer-sep { color: #d1d5db; }

        /* ══ BRAND SIDE ══ */
        .auth-brand-side {
          position: relative;
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: space-between;
          overflow: hidden;
          padding: 48px 40px;
        }

        .auth-geo-pattern {
          position: absolute;
          inset: 0;
          width: 100%; height: 100%;
          pointer-events: none;
        }

        /* ── Payment card illustration ── */
        .auth-card-illustration {
          position: relative;
          z-index: 1;
          width: 320px;
        }

        .auth-pay-card {
          width: 300px;
          height: 180px;
          border-radius: 20px;
          position: absolute;
        }
        .auth-pay-card--back {
          background: rgba(255,255,255,0.12);
          border: 1px solid rgba(255,255,255,0.2);
          top: 20px; left: 20px;
          transform: rotate(4deg);
          backdrop-filter: blur(8px);
        }
        .auth-pay-card--front {
          background: rgba(255,255,255,0.18);
          border: 1px solid rgba(255,255,255,0.3);
          top: 0; left: 0;
          backdrop-filter: blur(12px);
          padding: 22px 24px;
          display: flex; flex-direction: column; justify-content: space-between;
          position: relative;
          box-shadow: 0 20px 60px rgba(0,0,0,0.2);
        }

        .auth-pay-card-top { display: flex; justify-content: space-between; align-items: center; }
        .auth-pay-chip {
          width: 32px; height: 24px;
          background: rgba(255,255,255,0.35);
          border-radius: 5px;
          display: flex; flex-direction: column;
          justify-content: center; gap: 3px; padding: 4px 5px;
        }
        .auth-pay-chip-line { height: 2px; background: rgba(255,255,255,0.6); border-radius: 1px; }

        .auth-pay-number {
          font-size: 15px; font-weight: 500;
          color: rgba(255,255,255,0.9);
          letter-spacing: 2px;
          font-family: 'Courier New', monospace;
        }
        .auth-pay-card-bottom { display: flex; justify-content: space-between; align-items: flex-end; }
        .auth-pay-micro { font-size: 8px; color: rgba(255,255,255,0.5); letter-spacing: 1.5px; margin-bottom: 2px; }
        .auth-pay-name { font-size: 11px; font-weight: 600; color: rgba(255,255,255,0.9); letter-spacing: 0.5px; }

        /* Transaction pills */
        .auth-txn-pill {
          position: absolute;
          display: flex; align-items: center; gap: 10px;
          background: #ffffff;
          border-radius: 12px;
          padding: 10px 14px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.14);
          white-space: nowrap;
          animation: pillFloat 3s ease-in-out infinite alternate;
        }
        .auth-txn-pill--1 { bottom: -60px; left: -24px; animation-delay: 0s; }
        .auth-txn-pill--2 { bottom: -100px; right: -16px; animation-delay: 1.5s; }
        @keyframes pillFloat {
          from { transform: translateY(0); }
          to   { transform: translateY(-8px); }
        }

        .auth-txn-icon {
          width: 28px; height: 28px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .auth-txn-icon--green { background: #dcfce7; color: #16a34a; }
        .auth-txn-icon--blue  { background: #dbeafe; color: #2563eb; }

        .auth-txn-label { font-size: 11px; color: #6b7280; font-weight: 500; }
        .auth-txn-amount { font-size: 13px; color: #111827; font-weight: 700; }

        /* Brand bottom */
        .auth-brand-bottom {
          position: relative;
          z-index: 1;
          margin-top: 160px;
          text-align: center;
        }
        .auth-brand-logo {
          height: 100px; 
          width: auto; 
          object-fit: contain;
          margin-bottom: 20px;
          display: block; 
          margin-left: auto; 
          margin-right: auto;
        }
        .auth-brand-tagline {
          font-family: 'Fraunces', serif;
          font-size: clamp(18px, 2vw, 26px);
          font-weight: 300;
          font-style: italic;
          color: rgba(255,255,255,0.9);
          line-height: 1.45;
          margin-bottom: 28px;
        }
        .auth-trust-row {
          display: flex; gap: 10px; justify-content: center; flex-wrap: wrap;
        }
        .auth-trust-badge {
          display: flex; align-items: center; gap: 6px;
          background: rgba(255,255,255,0.1);
          border: 1px solid rgba(255,255,255,0.15);
          border-radius: 100px;
          padding: 6px 14px;
          font-size: 11.5px; font-weight: 500;
          color: rgba(255,255,255,0.8);
          backdrop-filter: blur(6px);
        }

        /* ══ RESPONSIVE ══ */

        /* Tablet */
        @media (max-width: 960px) {
          .auth-root { flex-direction: column; }
          .auth-brand-side {
            order: -1;
            min-height: 220px;
            padding: 32px 24px;
            justify-content: center;
          }
          .auth-card-illustration { display: none; }
          .auth-brand-bottom { margin-top: 0; }
          .auth-brand-tagline { font-size: 18px; }
          .auth-brand-logo { margin-bottom: 14px; }

          .auth-form-side { flex: 1; }
          .auth-desktop-topbar { display: none; }
          .auth-mobile-bar { display: flex; }
          .auth-form-center { padding: 36px 32px; }
          .auth-form-footer { padding: 16px 32px 24px; }
        }

        /* Mobile */
        @media (max-width: 600px) {
          .auth-brand-side {
            min-height: 160px;
            padding: 24px 20px;
          }
          .auth-brand-tagline { font-size: 15px; }
          .auth-trust-row { display: none; }
          .auth-brand-logo { height: 28px; margin-bottom: 10px; }

          .auth-form-center { padding: 28px 20px; }
          .auth-form-footer {
            padding: 12px 20px 20px;
            flex-wrap: wrap; gap: 6px;
            justify-content: center;
          }
          .auth-mobile-bar { padding: 16px 20px; min-height: 68px; }
          .auth-mobile-logo { height: 26px; }
        }
      `}</style>
    </div>
  );
}